import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, UserPlus, Check, X, User, Clock, UserCheck } from 'lucide-react';

const FriendsSection = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState('friends'); // 'friends' | 'requests' | 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sentRequests, setSentRequests] = useState(new Set());
  const debounceRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = () => localStorage.getItem('rbr_token');

  // Fetch friends list when tab opened
  useEffect(() => {
    if (activeTab === 'friends') fetchFriends();
    if (activeTab === 'search') fetchSearch(''); // discover list on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Debounced search-as-you-type when on the search tab
  useEffect(() => {
    if (activeTab !== 'search') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSearch(searchQuery);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeTab]);

  const fetchFriends = async () => {
    try {
      const response = await fetch(`${API_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await response.json();
      setFriends(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch friends:', e);
    }
  };

  const fetchSearch = async (q) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/users/search?query=${encodeURIComponent(q || '')}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const data = await response.json();
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Search failed:', e);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const sendFriendRequest = async (targetUserId) => {
    // Optimistically mark as sent
    setSentRequests((prev) => new Set(prev).add(targetUserId));
    try {
      const response = await fetch(`${API_URL}/api/friends/request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to send request');
        // Revert
        setSentRequests((prev) => {
          const n = new Set(prev);
          n.delete(targetUserId);
          return n;
        });
      }
    } catch (error) {
      console.error('Failed to send friend request:', error);
      setSentRequests((prev) => {
        const n = new Set(prev);
        n.delete(targetUserId);
        return n;
      });
    }
  };

  const cancelFriendRequest = async (targetUserId) => {
    // Optimistically clear the sent marker
    setSentRequests((prev) => {
      const n = new Set(prev);
      n.delete(targetUserId);
      return n;
    });
    // Flip any server-side "request_sent" row back to "none" locally
    setSearchResults((prev) =>
      prev.map((u) => (u.id === targetUserId ? { ...u, status: 'none' } : u))
    );
    try {
      const response = await fetch(`${API_URL}/api/friends/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId }),
      });
      if (!response.ok) {
        // Revert on error
        setSentRequests((prev) => new Set(prev).add(targetUserId));
        setSearchResults((prev) =>
          prev.map((u) => (u.id === targetUserId ? { ...u, status: 'request_sent' } : u))
        );
      }
    } catch (error) {
      console.error('Failed to cancel friend request:', error);
      setSentRequests((prev) => new Set(prev).add(targetUserId));
      setSearchResults((prev) =>
        prev.map((u) => (u.id === targetUserId ? { ...u, status: 'request_sent' } : u))
      );
    }
  };

  const respondToRequest = async (requesterId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/friends/respond`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ requesterId, action }),
      });
      if (response.ok) {
        onUpdateUser && onUpdateUser();
        if (action === 'accept') fetchFriends();
      }
    } catch (error) {
      console.error('Failed to respond to request:', error);
    }
  };

  const pendingRequests = user?.friendRequests?.filter((r) => r.status === 'pending') || [];

  const renderActionButton = (u) => {
    if (u.status === 'friend') {
      return (
        <span className="flex items-center gap-1 text-xs text-emerald-400 font-medium">
          <UserCheck className="w-4 h-4" /> Friends
        </span>
      );
    }
    if (u.status === 'incoming_request') {
      return (
        <span className="text-[10px] text-stone-500 italic">Check Requests tab</span>
      );
    }
    if (u.status === 'request_sent' || sentRequests.has(u.id)) {
      return (
        <button
          onClick={() => cancelFriendRequest(u.id)}
          className="group flex items-center gap-1 text-xs text-stone-500 hover:text-red-400 font-medium px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors"
          data-testid={`friend-req-cancel-${u.id}`}
          title="Cancel friend request"
        >
          <Clock className="w-4 h-4 group-hover:hidden" />
          <X className="w-4 h-4 hidden group-hover:block" />
          <span className="group-hover:hidden">Sent</span>
          <span className="hidden group-hover:inline">Undo</span>
        </button>
      );
    }
    return (
      <button
        onClick={() => sendFriendRequest(u.id)}
        className="p-2 bg-gradient-to-br from-[#D6AD60] to-[#D6AD60] text-[#1a1814] rounded-full hover:from-[#D6AD60] hover:to-[#D6AD60] transition-colors shadow-md"
        data-testid={`friend-add-btn-${u.id}`}
        aria-label="Add friend"
      >
        <UserPlus className="w-4 h-4" />
      </button>
    );
  };

  return (
    <div className="bg-[#FFFCF3] border border-stone-200 rounded-2xl shadow-md p-4" data-testid="friends-section">
      <h3 className="text-lg font-semibold text-stone-800 mb-4">Friends</h3>

      <div className="flex gap-2 mb-4 border-b border-stone-200">
        <button
          onClick={() => setActiveTab('friends')}
          className={`pb-2 px-3 text-sm font-medium transition-colors ${
            activeTab === 'friends' ? 'text-[#D6AD60] border-b-2 border-[#D6AD60]' : 'text-stone-500 hover:text-stone-800'
          }`}
          data-testid="friends-tab-friends"
        >
          Friends ({friends.length})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`pb-2 px-3 text-sm font-medium transition-colors relative ${
            activeTab === 'requests' ? 'text-[#D6AD60] border-b-2 border-[#D6AD60]' : 'text-stone-500 hover:text-stone-800'
          }`}
          data-testid="friends-tab-requests"
        >
          Requests
          {pendingRequests.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {pendingRequests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`pb-2 px-3 text-sm font-medium transition-colors ${
            activeTab === 'search' ? 'text-[#D6AD60] border-b-2 border-[#D6AD60]' : 'text-stone-500 hover:text-stone-800'
          }`}
          data-testid="friends-tab-discover"
        >
          Discover
        </button>
      </div>

      {activeTab === 'friends' && (
        <div className="space-y-3">
          {friends.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No friends yet. Head to Discover to add some!</p>
          ) : (
            friends.map((friend) => (
              <Link
                to={`/user/${friend.id}`}
                key={friend.id}
                className="flex items-center gap-3 p-3 bg-[#E8DDCB] rounded-lg hover:bg-[#DBC9A3] transition-colors"
                data-testid={`friend-row-${friend.id}`}
              >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
                  {friend.profilePicture ? (
                    <img src={friend.profilePicture} alt={friend.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 truncate">{friend.name}</p>
                </div>
                <div className="text-xs text-[#D6AD60]">🔥 {friend.streak?.current || 0}</div>
              </Link>
            ))
          )}
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="space-y-3">
          {pendingRequests.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No pending requests</p>
          ) : (
            pendingRequests.map((request) => (
              <div key={request.userId} className="flex items-center gap-3 p-3 bg-[#E8DDCB] rounded-lg" data-testid={`friend-request-${request.userId}`}>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
                  {request.userProfilePicture ? (
                    <img src={request.userProfilePicture} alt={request.userName} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 truncate">{request.userName}</p>
                  <p className="text-xs text-stone-500">Friend request</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => respondToRequest(request.userId, 'accept')}
                    className="p-2 bg-emerald-500 text-white rounded-full hover:bg-emerald-400 transition-colors"
                    data-testid={`friend-accept-${request.userId}`}
                    aria-label="Accept request"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => respondToRequest(request.userId, 'reject')}
                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    data-testid={`friend-reject-${request.userId}`}
                    aria-label="Reject request"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'search' && (
        <div>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name..."
              className="w-full pl-10 pr-4 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              data-testid="friends-search-input"
            />
          </div>

          {!searchQuery && (
            <p className="text-xs text-[#D6AD60] mb-3 font-medium uppercase tracking-wider">Discover people</p>
          )}

          <div className="space-y-3">
            {loading ? (
              <p className="text-center text-stone-500 py-6 text-sm">Loading...</p>
            ) : searchResults.length === 0 ? (
              <p className="text-center text-stone-500 py-8 text-sm">
                {searchQuery ? 'No users found' : 'No other users yet — invite a friend to join!'}
              </p>
            ) : (
              searchResults.map((foundUser) => (
                <div key={foundUser.id} className="flex items-center gap-3 p-3 bg-[#E8DDCB] rounded-lg" data-testid={`discover-row-${foundUser.id}`}>
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
                    {foundUser.profilePicture ? (
                      <img src={foundUser.profilePicture} alt={foundUser.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800 truncate">{foundUser.name}</p>
                    <p className="text-xs text-[#D6AD60]">🔥 {foundUser.streak?.current || 0} day streak</p>
                  </div>
                  {renderActionButton(foundUser)}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendsSection;
